import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useFirestoreGallery, GalleryCategory } from '../hooks/useFirestoreGallery';

const Gallery: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { categories, loading } = useFirestoreGallery();
  const [selectedCategory, setSelectedCategory] = useState<GalleryCategory | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const openGallery = (category: GalleryCategory) => {
    if (category.images.length > 0) {
      setSelectedCategory(category);
      setSelectedImageIndex(0);
    }
  };

  const closeGallery = () => {
    setSelectedCategory(null);
    setSelectedImageIndex(0);
  };

  const nextImage = () => {
    if (selectedCategory && selectedCategory.images) {
      setSelectedImageIndex((prev) =>
        prev === selectedCategory.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedCategory && selectedCategory.images) {
      setSelectedImageIndex((prev) =>
        prev === 0 ? selectedCategory.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <section id="gallery" className="p-16 bg-brand-light">
      <div className="container">
        <motion.div
          ref={ref}
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-brand-pink mb-8">Our Learning Environment</h2>
          <p className="text-lg text-brand-purple mb-8">Glimpses of our screen-free, hands-on learning spaces</p>
        </motion.div>
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-purple mx-auto"></div>
            <p className="mt-4 text-brand-blue">Loading gallery...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                className="group cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                onClick={() => openGallery(category)}
              >
                <div className={`relative overflow-hidden rounded-3xl bg-${category.color} bg-opacity-10 h-64 flex items-center justify-center border-2 border-${category.color} group-hover:border-brand-pink transition-all duration-300 shadow-bubble`}>
                  <div className="text-center p-6">
                    <div className={`w-16 h-16 bg-${category.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 text-3xl shadow-brand`}>
                      <span>{category.icon}</span>
                    </div>
                    <span className="text-brand-dark font-bold group-hover:text-brand-pink transition-colors duration-300 text-lg block mb-2">
                      {category.label}
                    </span>
                    <span className="text-brand-blue text-sm">
                      {category.images.length} {category.images.length === 1 ? 'photo' : 'photos'}
                    </span>
                  </div>
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-brand-pink/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center rounded-3xl">
                    <span className="text-brand-white font-bold text-lg">
                      {category.images.length > 0 ? 'View Photos' : 'No Photos Yet'}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedCategory && selectedCategory.images.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
            onClick={closeGallery}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-12 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{selectedCategory.icon}</span>
                    <div>
                      <h3 className="text-2xl font-bold text-brand-dark">{selectedCategory.label}</h3>
                      <p className="text-brand-blue">
                        {selectedImageIndex + 1} / {selectedCategory.images.length}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    onClick={closeGallery}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ✕
                  </motion.button>
                </div>
              </div>

              {/* Image Gallery */}
              <div className="relative overflow-hidden bg-gray-100">
                <img
                  src={selectedCategory.images[selectedImageIndex].url}
                  alt={selectedCategory.label}
                  className="w-full h-64 md:h-96 object-contain"
                />

                {/* Navigation Arrows */}
                {selectedCategory.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 rounded-full p-2 text-brand-dark hover:bg-opacity-100"
                    >
                      ←
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 rounded-full p-2 text-brand-dark hover:bg-opacity-100"
                    >
                      →
                    </button>
                  </>
                )}

                {/* Image Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {selectedCategory.images.map((image, index) => (
                    <button
                      key={image.public_id}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`w-3 h-3 rounded-full ${
                        index === selectedImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery; 